import { Injectable } from '@angular/core';
import {
  EstimationTimeSlotsDetails,
  MappedRequestTimeWindow,
  RequestTimeWindowFilter,
} from '@core/interfaces/requests-time-windows.interface';
import {
  State, Selector, Action, StateContext,
} from '@ngxs/store';
import { tap, catchError, of } from 'rxjs';
import { RequestsTimeWindowsService } from '@core/services/requests-time-windows.service';
import { mapRequestTimeWindowData } from '@core/helper/request-time-window-helper';
import { BaseSortedEntityListStateWithDateFilter } from '@core/interfaces/base-entity-list-state.interface';
import { getDefaultPaginatedEntityListStateWithDateFilter } from '@core/helper/state-helper';
import { DateRange, NgbDateRange } from '@core/interfaces/date-filter.interface';
import { OffsetPagination } from '@core/interfaces/pagination-params.interface';
import { SortEnum } from '@core/enums/sort.enum';
import { SortInterface } from '@core/interfaces/get-list.interface';
import { gateDateRangeFromNgbDateRange } from '@core/helper/date-helper';
import {
  GetEstimationTimeSlotsDetails,
  GetListRequestTimeWindow,
  SetRequestTimeWindowDateFilter,
  SetRequestTimeWindowFilter,
  SetRequestTimeWindowPaginationParams,
  SetRequestTimeWindowSortParams,
} from '../action/requests-time-windows.action';

export interface RequestTimeWindowStateModel extends BaseSortedEntityListStateWithDateFilter<
  MappedRequestTimeWindow,
  RequestTimeWindowFilter
> {
  estimationDetails: EstimationTimeSlotsDetails | null;
  estimationDetailsError: string | null;
}

@State<RequestTimeWindowStateModel>({
  name: 'requestTimeWindow',
  defaults: {
    ...getDefaultPaginatedEntityListStateWithDateFilter<RequestTimeWindowFilter, MappedRequestTimeWindow>(),
    sort: { field: 'createdAt', order: SortEnum.Desc },
    estimationDetails: null,
    estimationDetailsError: null,
  },
})
@Injectable()
export class RequestTimeWindowState {
  constructor(private requestsTimeWindowsService: RequestsTimeWindowsService) {}

  @Selector()
  static requestTimeWindows(state: RequestTimeWindowStateModel): MappedRequestTimeWindow[] {
    return state.list;
  }

  @Selector()
  static getTotalRequestTimeWindows(state: RequestTimeWindowStateModel): number {
    return state.total;
  }

  @Selector()
  static error(state: RequestTimeWindowStateModel): string | null {
    return state.error;
  }

  @Selector()
  static getFilter(state: RequestTimeWindowStateModel): RequestTimeWindowFilter {
    return state.filter;
  }

  @Selector()
  static getRawDateFilter(state: RequestTimeWindowStateModel): NgbDateRange {
    return state.rawDateFilter;
  }

  @Selector()
  static getDateFilter(state: RequestTimeWindowStateModel): DateRange {
    return gateDateRangeFromNgbDateRange(state.rawDateFilter);
  }

  @Selector()
  static getPaginationParams(state: RequestTimeWindowStateModel): OffsetPagination {
    return state.pagination;
  }

  @Selector()
  static sortParams(state: RequestTimeWindowStateModel): SortInterface {
    return state.sort;
  }

  @Selector()
  static estimationDetails(state: RequestTimeWindowStateModel): EstimationTimeSlotsDetails | null {
    return state.estimationDetails;
  }

  @Selector()
  static estimationDetailsError(state: RequestTimeWindowStateModel): string | null {
    return state.estimationDetailsError;
  }

  @Action(GetListRequestTimeWindow)
  getListRequestTimeWindow(ctx: StateContext<RequestTimeWindowStateModel>, { params }: GetListRequestTimeWindow): any {
    ctx.patchState({ error: null });
    return this.requestsTimeWindowsService.getListRequestTimeWindow(params).pipe(
      tap((result) => {
        const requestTimeWindows = result.data.getListTimeSlotsEstimation.result;

        const mappedData = mapRequestTimeWindowData(requestTimeWindows);

        ctx.patchState({
          list: mappedData,
          total: result.data.getListTimeSlotsEstimation.total,
        });
      }),
      catchError((error) => {
        ctx.patchState({ error: error.message });
        return of(null);
      }),
    );
  }

  @Action(SetRequestTimeWindowFilter)
  public setFilter(ctx: StateContext<RequestTimeWindowStateModel>, { filter }: SetRequestTimeWindowFilter): void {
    const filterToUse = {
      ...ctx.getState().filter,
      ...filter,
    };
    ctx.patchState({
      filter: filterToUse,
    });
  }

  @Action(SetRequestTimeWindowDateFilter)
  public setDateFilter(
    ctx: StateContext<RequestTimeWindowStateModel>,
    { dateFilter }: SetRequestTimeWindowDateFilter,
  ): void {
    ctx.patchState({
      rawDateFilter: dateFilter,
    });
  }

  @Action(SetRequestTimeWindowPaginationParams)
  public setPaginationParams(
    ctx: StateContext<RequestTimeWindowStateModel>,
    { paginationParams }: SetRequestTimeWindowPaginationParams,
  ): void {
    ctx.patchState({
      pagination: paginationParams,
    });
  }

  @Action(SetRequestTimeWindowSortParams)
  public setRequestTimeWindowSortParams(
    ctx: StateContext<RequestTimeWindowStateModel>,
    { sortParams }: SetRequestTimeWindowSortParams,
  ): void {
    ctx.patchState({
      sort: sortParams,
    });
  }

  @Action(GetEstimationTimeSlotsDetails)
  getEstimationTimeSlotsDetails(
    ctx: StateContext<RequestTimeWindowStateModel>,
    { id }: GetEstimationTimeSlotsDetails,
  ): any {
    ctx.patchState({ estimationDetails: null, estimationDetailsError: null });
    return this.requestsTimeWindowsService.getEstimationTimeSlotsDetails(id).pipe(
      tap((result) => {
        ctx.patchState({ estimationDetails: result.data.estimationTimeSlotsDetails });
      }),
      catchError((error) => {
        ctx.patchState({ estimationDetailsError: error.message });
        return of(null);
      }),
    );
  }
}
